/**
 * Location bias for the Google Places address box.
 *
 * Google ranks predictions by how likely they are from where it thinks the
 * request comes from, which for a server-side proxy or a VPN is nowhere near
 * the visitor. "350 5th Ave, New York" then lists Brooklyn and Troy before
 * Manhattan, and a visitor who presses Enter books the wrong borough. A
 * `bounds` hint fixes the ranking without restricting it (`strictBounds` stays
 * off), so an address outside the circle is still offered - just lower.
 *
 * Where the hint comes from, in order:
 * 1. the marketing-site hand-off: `lat`/`lng` of the address the visitor
 *    picked there, or the `city` + `province` it named (usePrefilledAddress
 *    stores whichever it got so the bias survives a Back to this step);
 * 2. failing that, the visitor's country from `navigator.language`
 *    (`en-CA` -> Canada), when it is one of the two countries we serve.
 *
 * Plain numbers only: this runs before the Maps script is on the page, so it
 * cannot build a `google.maps.LatLngBounds`, and the Autocomplete widget
 * accepts the literal form.
 */

export interface LatLngBoundsLiteral {
  north: number;
  south: number;
  east: number;
  west: number;
}

/** What the hand-off can tell us about where the visitor is. */
export interface PlacesBiasHint {
  lat?: number | null;
  lng?: number | null;
  city?: string | null;
  province?: string | null;
}

/** sessionStorage key usePrefilledAddress keeps the hand-off hint under. */
export const PLACES_BIAS_KEY = 'bookingBias';

/** Radius of the circle drawn around a hand-off point, in km. */
export const BIAS_RADIUS_KM = 50;

const KM_PER_DEGREE_LAT = 111.32;

/**
 * Centres of the cities the marketing site links to the portal from, for a
 * hand-off that names a city but carries no coordinates. Same figures as the
 * site's own city pages (aperio/site/src/data/cities.js). A city not listed
 * here simply gets no bias, never a wrong one.
 */
const CITY_CENTRES: Record<string, { lat: number; lng: number }> = {
  'winnipeg|mb': { lat: 49.8951, lng: -97.1384 },
  'toronto|on': { lat: 43.6532, lng: -79.3832 },
  'montreal|qc': { lat: 45.5019, lng: -73.5674 },
  'montréal|qc': { lat: 45.5019, lng: -73.5674 },
  'vancouver|bc': { lat: 49.2827, lng: -123.1207 },
  'calgary|ab': { lat: 51.0447, lng: -114.0719 },
  'edmonton|ab': { lat: 53.5461, lng: -113.4938 },
  'ottawa|on': { lat: 45.4215, lng: -75.6972 },
  'saskatoon|sk': { lat: 52.1332, lng: -106.67 },
  'regina|sk': { lat: 50.4452, lng: -104.6189 },
  'hamilton|on': { lat: 43.2557, lng: -79.8711 },
  'victoria|bc': { lat: 48.4284, lng: -123.3656 },
  'quebec city|qc': { lat: 46.8139, lng: -71.208 },
  'québec|qc': { lat: 46.8139, lng: -71.208 },
  'halifax|ns': { lat: 44.6488, lng: -63.5752 },
  'minneapolis|mn': { lat: 44.9778, lng: -93.265 },
  'seattle|wa': { lat: 47.6062, lng: -122.3321 },
};

/**
 * Bounding boxes of the two countries the address box is restricted to. Wide
 * on purpose: this is the weakest hint, only there so that a Canadian visitor
 * typing a street that exists on both sides of the border sees the Canadian
 * one first.
 */
const COUNTRY_BOUNDS: Record<string, LatLngBoundsLiteral> = {
  CA: {
    north: 83.11, south: 41.68, east: -52.62, west: -141.0,
  },
  US: {
    north: 49.38, south: 24.4, east: -66.93, west: -124.85,
  },
};

function finite(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/** A box of roughly `radiusKm` around a point, clamped to valid ranges. */
export function circleBounds(
  lat: number,
  lng: number,
  radiusKm = BIAS_RADIUS_KM,
): LatLngBoundsLiteral {
  const dLat = radiusKm / KM_PER_DEGREE_LAT;
  const cos = Math.max(Math.cos((lat * Math.PI) / 180), 0.01);
  const dLng = radiusKm / (KM_PER_DEGREE_LAT * cos);
  return {
    north: Math.min(lat + dLat, 90),
    south: Math.max(lat - dLat, -90),
    east: Math.min(lng + dLng, 180),
    west: Math.max(lng - dLng, -180),
  };
}

/**
 * The region of a BCP 47 language tag (`en-CA` -> `CA`), upper-cased, or
 * null when it has none or names a country we do not serve.
 */
export function countryFromLanguage(
  language?: string | null,
): string | null {
  const parts = (language || '').split(/[-_]/);
  const region = (parts[1] || '').toUpperCase();
  return region && COUNTRY_BOUNDS[region] ? region : null;
}

/** The bounding box for a country we serve, or null. */
export function countryBounds(
  country?: string | null,
): LatLngBoundsLiteral | null {
  const code = (country || '').toUpperCase();
  return COUNTRY_BOUNDS[code] || null;
}

/** Bounds for a hand-off hint, or null when it holds nothing usable. */
export function boundsForHint(
  hint?: PlacesBiasHint | null,
): LatLngBoundsLiteral | null {
  if (!hint) {
    return null;
  }
  if (finite(hint.lat) && finite(hint.lng)
    && !(hint.lat === 0 && hint.lng === 0)) {
    return circleBounds(hint.lat, hint.lng);
  }
  const key = `${(hint.city || '').trim().toLowerCase()}|${
    (hint.province || '').trim().toLowerCase()}`;
  const centre = CITY_CENTRES[key];
  return centre ? circleBounds(centre.lat, centre.lng) : null;
}

/** True when the hint would give a bias, i.e. is worth remembering. */
export function hasUsableHint(hint?: PlacesBiasHint | null): boolean {
  return boundsForHint(hint) !== null;
}

export function saveBiasHint(hint: PlacesBiasHint): void {
  try {
    window.sessionStorage.setItem(PLACES_BIAS_KEY, JSON.stringify(hint));
  } catch (e) {
    // sessionStorage unavailable (private mode) - the bias is best effort
  }
}

export function loadBiasHint(): PlacesBiasHint | null {
  try {
    const raw = window.sessionStorage.getItem(PLACES_BIAS_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (e) {
    return null;
  }
}

/**
 * The bias the address box should use right now: the hand-off hint (given,
 * or remembered from earlier in this session), else the visitor's country.
 */
export function resolvePlacesBounds(
  hint?: PlacesBiasHint | null,
  language: string | null | undefined = typeof navigator !== 'undefined'
    ? navigator.language : null,
): LatLngBoundsLiteral | null {
  return boundsForHint(hint)
    || boundsForHint(loadBiasHint())
    || countryBounds(countryFromLanguage(language));
}
